import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Truck, Thermometer, ClipboardCheck, ArrowLeft, Snowflake } from "lucide-react";
import api from "@/lib/api";

/**
 * Landing page reached when a driver scans a vehicle QR (URL:
 * <BASE>/app/vehicle-scan/<eq_id>). The page loads the equipment record,
 * detects whether it's a Cold Truck (has_operating_range=true — both
 * temperature + cleanliness) or a Delivery Truck (cleanliness only),
 * and renders the appropriate action buttons.
 *
 * Phase 3 will wire the "Daily cleanliness" button to the actual
 * vehicle-clean check form. For now it navigates to a placeholder route
 * that the check module will pick up.
 */
export default function VehicleScan() {
  const { eqId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, err: "", eq: null, type: null, site: null });

  useEffect(() => {
    (async () => {
      try {
        const [eqRes, typesRes, sitesRes] = await Promise.all([
          api.get("/equipment"),
          api.get("/equipment-types"),
          api.get("/sites"),
        ]);
        const eq = (eqRes.data.equipment || []).find((e) => e.id === eqId);
        if (!eq) throw new Error("This vehicle is not available on your account.");
        const type = (typesRes.data.equipment_types || []).find((t) => t.id === eq.equipment_type_id);
        const site = (sitesRes.data.sites || []).find((s) => s.id === eq.site_id);
        // Delivery Truck has no temperature capture — skip the chooser
        // entirely and drop the driver straight onto the cleanliness
        // tab pre-scoped to this vehicle.
        if ((type?.name || "").toLowerCase() === "delivery truck") {
          navigate(`/app/daily-tracking?tab=vehicles&scope=equipment&equipmentId=${eq.id}`, { replace: true });
          return;
        }
        setState({ loading: false, err: "", eq, type, site });
      } catch (e) {
        // Prefer the friendlier hard-coded copy over server-side generic
        // detail messages ("Something went wrong.") on this scan landing
        // page — drivers on their phones need clear guidance.
        setState({
          loading: false,
          err: "This vehicle QR isn't tied to any equipment on your account.",
          eq: null, type: null, site: null,
        });
      }
    })();
  }, [eqId, navigate]);

  if (state.loading) {
    return <Shell><div className="opacity-60">Loading…</div></Shell>;
  }
  if (state.err) {
    return (
      <Shell>
        <div className="vb-pill err" data-testid="veh-scan-error">{state.err}</div>
        <button className="vb-btn ghost sm mt-3" onClick={() => navigate("/app")}>
          <ArrowLeft size={13} /> Back to dashboard
        </button>
      </Shell>
    );
  }

  const { eq, type, site } = state;
  const isCold = (type?.name || "").toLowerCase() === "cold truck";

  return (
    <Shell>
      <div className="vb-card p-5" data-testid="vehicle-scan-card">
        <div className="flex items-center gap-2 mb-1">
          {isCold ? <Snowflake size={22} style={{ color: "#2563eb" }} /> : <Truck size={22} style={{ color: "var(--vb-accent)" }} />}
          <h1 className="vb-heading text-2xl m-0" style={{ color: "var(--vb-text-2)" }}>{eq.name}</h1>
        </div>
        <div className="opacity-70 text-sm mb-4" data-testid="veh-scan-subtitle">
          {type?.name || "Vehicle"} · {site?.name || "—"}
        </div>

        <p className="text-sm opacity-80 mb-3">What would you like to do?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {isCold && (
            <ActionTile
              icon={Thermometer}
              testid="veh-action-temp"
              title="Log temperature"
              subtitle={`Target ${type?.min_value ?? "?"}–${type?.max_value ?? "?"}${type?.parameter_unit || " °C"}`}
              onClick={() => navigate(`/app/log?equipmentId=${eq.id}`)}
              accent="#2563eb"
            />
          )}
          <ActionTile
            icon={ClipboardCheck}
            testid="veh-action-clean"
            title="Daily cleanliness check"
            subtitle="Complete today's checklist"
            onClick={() => navigate(`/app/daily-tracking?tab=vehicles&scope=equipment&equipmentId=${eq.id}`)}
            accent="var(--vb-accent)"
          />
        </div>

        <button className="vb-btn ghost sm mt-6" onClick={() => navigate("/app")} data-testid="veh-scan-back">
          <ArrowLeft size={13} /> Back to dashboard
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="vb-fade-in" style={{ maxWidth: 720, margin: "0 auto" }}>
      {children}
    </div>
  );
}

function ActionTile({ icon: Icon, title, subtitle, onClick, accent, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="vb-card p-4 text-left hover:shadow-md transition-shadow"
      style={{ cursor: "pointer", borderLeft: `4px solid ${accent}` }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={18} style={{ color: accent }} />
        <strong className="text-[14px]" style={{ color: "var(--vb-text-2)" }}>{title}</strong>
      </div>
      <div className="text-[12px] opacity-70">{subtitle}</div>
    </button>
  );
}
